import { tokenValuesQueryOptions } from "@/api/data";
import { mySupportersQuery, myWalletQueryOptions } from "@/api/me";
import { centsToDollars } from "@common/utils/currency";
import { formatCents, formatDollars } from "@common/utils/i18n";
import { CurrencyWallet, MySupporterEap, MyWalletApproxLocalValue } from "@givenwell/marketplace-api";
import { useQuery } from "@tanstack/react-query";

type TokenCurrencyCode = CurrencyWallet["token_currency"];

const currencyCodeMap: Record<string, string> = {
  auarrears: "AUD",
  autoken: "AUD",
  catoken: "CAD",
  eutoken: "EUR",
  gbtoken: "GBP",
  nzarrears: "NZD",
  nztoken: "NZD",
  sgtoken: "SGD",
  usarrears: "USD",
  ustoken: "USD",
  aetoken: "AED",
  iltoken: "ILS",
  mxtoken: "MXN",
  hktoken: "HKD",
  cntoken: "CNY",
} satisfies Record<TokenCurrencyCode, string>;

const sum = (nums: number[]) => nums.reduce((a, b) => a + b, 0);

/** The per-session repayment cap, with the source figure it was derived from when FX-converted. */
export type EapRepaymentCap = {
  /** Resolved cap in the member's currency, in the smallest unit of denomination. */
  maxValueCents: number;
  /** The member's (resolved) currency, e.g. "INR". */
  currencyCode: string;
  /** True when the resolved cap is an FX-converted approximation rather than an exact configured limit. */
  isEstimate: boolean;
  /** The configured cap the repayment is based on, in the smallest unit of its own denomination. */
  sourceValueCents: number;
  /** The currency the configured cap is set in, e.g. "NZD". */
  sourceCurrencyCode: string;
};

/** Maps the raw EAP DTO into the repayment-cap shape used for display. */
export function eapRepaymentCapFromDto(eap: MySupporterEap): EapRepaymentCap {
  return {
    maxValueCents: eap.max_tokens_price_cents,
    currencyCode: eap.max_tokens_price_currency.toUpperCase(),
    isEstimate: eap.max_price_is_estimate,
    sourceValueCents: eap.source_price_cents,
    sourceCurrencyCode: eap.source_price_currency.toUpperCase(),
  };
}

/**
 * Renders the per-session repayment cap for display: just the resolved amount when the supporter's limit
 * is set in the member's own currency (e.g. "₹13,000"), or the source amount plus an approximation when it
 * has been FX-converted from another currency (e.g. "NZ$250 (approx. ₹13,000)").
 */
export function formatEapRepaymentCap(cap: Partial<EapRepaymentCap> | undefined): string {
  const currencyCode = cap?.currencyCode || "NZD";
  const resolved = formatCents(cap?.maxValueCents || 0, currencyCode);

  if (cap?.isEstimate && cap.sourceCurrencyCode && cap.sourceCurrencyCode !== currencyCode) {
    const source = formatCents(cap.sourceValueCents || 0, cap.sourceCurrencyCode);
    return `${source} (approx. ${resolved})`;
  }

  return resolved;
}

function useMemberEAPBenefits() {
  const supportersQuery = useQuery({ ...mySupportersQuery() });

  if (!supportersQuery.data) return undefined;

  const eapSupporters = supportersQuery.data.supporters.filter(s => s.eap && s.eap.available_sessions > 0) ?? [];

  if (eapSupporters.length === 0) return undefined;

  const supportersSortedByValue = [...eapSupporters].sort(
    (a, b) => b.eap!.max_tokens_price_cents - a.eap!.max_tokens_price_cents,
  );
  const mostValuableSupporter = supportersSortedByValue[0]!;
  const eap = mostValuableSupporter.eap!;

  const availableSessions = sum(eapSupporters.map(s => s.eap?.available_sessions || 0));

  if (!eap.max_tokens_price_cents) {
    return undefined;
  }
  return {
    ...eapRepaymentCapFromDto(eap),
    /** Whether any EAP supporter's plan lets the member claim back sessions with their own provider. */
    canSubmitClaims: eapSupporters.some(s => s.can_submit_eap_claims),
    supporterId: mostValuableSupporter.supporter_id,
    availableSessions: availableSessions,
  };
}

/** The allowance balance, plus the same figure in the member's own currency when the two differ. */
export type MemberAllowanceBenefits = {
  /** The balance in the wallet's own currency, which is what the tokens are denominated in. */
  balanceDollars: number;
  /** The wallet's currency, e.g. "USD". */
  currencyCode: string | undefined;
  /** The balance FX-converted into the member's own currency; absent when no conversion applies. */
  approxLocalValue?: MyWalletApproxLocalValue;
};

/** The allowance balance as it should read on screen, split so the two currencies never share a line. */
export type AllowanceBalanceDisplay = {
  /** The headline figure, in the currency the member actually spends in wherever we can quote one. */
  balance: string;
  /** Caption accounting for a converted headline. Absent when only one currency is in play. */
  conversionNote?: string;
  /** Longer copy for the help affordance beside the note. Present exactly when the note is. */
  conversionHelp?: string;
};

/**
 * Describes the allowance balance for display. Members in markets with no token currency of their own
 * (e.g. the Philippines) hold a wallet denominated in another country's currency, so we lead with the
 * currency they actually spend in and account for the wallet figure underneath:
 *
 *     ~₱3,048     This is an approximation only. Your wallet holds US$50.00.
 *
 * Everyone else has one currency in play, so they get the wallet amount and nothing to explain.
 */
export function describeAllowanceBalance(allowance: MemberAllowanceBenefits | undefined): AllowanceBalanceDisplay {
  const walletCurrency = allowance?.currencyCode || "NZD";
  const walletBalance = formatDollars(allowance?.balanceDollars || 0, walletCurrency);

  const local = allowance?.approxLocalValue;
  if (!local) return { balance: walletBalance };

  const localCurrency = local.currency.toUpperCase();
  const localDollars = centsToDollars(local.cents, localCurrency);

  return {
    // Tilde and no cents: an indicative market rate isn't a figure to quote to the cent.
    balance: `~${formatDollars(localDollars, localCurrency, { maximumFractionDigits: 0 })}`,
    // The rate itself is noise here — what matters is that the figure is soft and which one is hard.
    conversionNote: `This is an approximation only. Your wallet holds ${walletBalance}`,
    conversionHelp:
      `Your allowance is funded in ${walletCurrency}, so we show what it's worth in ${localCurrency} ` +
      `at today's exchange rate. That rate moves daily, so treat this as a guide — the amount you're ` +
      `paid is set by the rate on the day your claim goes through.`,
  };
}

function useMemberAllowanceBenefits(): MemberAllowanceBenefits | undefined {
  const walletQuery = useQuery(myWalletQueryOptions);
  const tokenValuesQuery = useQuery(tokenValuesQueryOptions);

  const tokenCurrency = walletQuery.data?.token_currency;
  const tokenValues = tokenValuesQuery.data;
  const tokenValue = tokenValues?.[tokenCurrency || ""];

  const balanceTokens = walletQuery.data?.token_balance || 0;
  const multiplier = tokenValue?.value || 0;

  const balanceDollars = balanceTokens * multiplier;

  if (!walletQuery.data) return undefined;

  return {
    balanceDollars: balanceDollars,
    currencyCode: currencyCodeMap[tokenCurrency!],
    // Set by the server only when the member's own currency differs from the wallet's.
    approxLocalValue: walletQuery.data.approx_local_value,
  };
}

export function useMemberBenefits() {
  const eap = useMemberEAPBenefits();
  const allowance = useMemberAllowanceBenefits();
  return { eap, allowance };
}
